import React, { useState, useEffect, useCallback } from 'react';
import {
  Ticket, Plus, PencilSimple, Trash, FilePdf, EnvelopeSimple, Money,
  Warning, CheckCircle, X, ChartLineUp, Calendar, Paperclip, ImageSquare, DownloadSimple, MagicWand,
} from '@phosphor-icons/react';
import { ticketApi, vehicleApi, customerApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const OFFENSE_TYPES = [
  { value: 'parken',          label: 'Parkverstoß' },
  { value: 'geschwindigkeit', label: 'Geschwindigkeit' },
  { value: 'rotlicht',        label: 'Rotlicht' },
  { value: 'handynutzung',    label: 'Handynutzung' },
  { value: 'abstand',         label: 'Abstand' },
  { value: 'sonstige',        label: 'Sonstige' },
];

const STATUS_LABEL = {
  received: 'Eingegangen', authority_informed: 'Halterauskunft erteilt',
  customer_notified: 'An Kunde weitergeleitet', paid: 'Bezahlt', closed: 'Erledigt',
};
const STATUS_STYLE = {
  received: 'bg-[#E60000]/10 text-[#E60000]',
  authority_informed: 'bg-[#FFB800]/15 text-[#B8860B]',
  customer_notified: 'bg-[#3B82F6]/10 text-[#3B82F6]',
  paid: 'bg-[#009951]/10 text-[#009951]',
  closed: 'bg-[#525866]/15 text-[#525866]',
};

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

const emptyForm = {
  offense_date: new Date().toISOString().substring(0, 16),
  offense_type: 'parken',
  offense_location: '',
  offense_description: '',
  authority: '',
  case_number: '',
  fine_amount: 0,
  administrative_fee: 0,
  processing_fee: 25,
  vehicle_id: '',
  customer_id: '',
  booking_id: '',
  notes: '',
};

const TrafficTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(null); // file_id being extracted
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    try {
      const [ts, st, rm, vh, cs] = await Promise.all([
        ticketApi.getAll(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ticketApi.stats(),
        ticketApi.reminders(),
        vehicleApi.getAll(),
        customerApi.getAll(),
      ]);
      setTickets(ts.data || []);
      setStats(st.data);
      setReminders(rm.data);
      setVehicles(vh.data || []);
      setCustomers(cs.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingId(null); setForm(emptyForm); setAttachments([]); setShowForm(true);
  };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      offense_date: (t.offense_date || '').substring(0, 16) || emptyForm.offense_date,
      offense_type: t.offense_type || 'parken',
      offense_location: t.offense_location || '',
      offense_description: t.offense_description || '',
      authority: t.authority || '',
      case_number: t.case_number || '',
      fine_amount: t.fine_amount || 0,
      administrative_fee: t.administrative_fee || 0,
      processing_fee: t.processing_fee || 25,
      vehicle_id: t.vehicle_id || '',
      customer_id: t.customer_id || '',
      booking_id: t.booking_id || '',
      notes: t.notes || '',
    });
    setAttachments(t.attachments || []);
    setShowForm(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!editingId) {
      toast.error('Bitte erst speichern, dann Anhänge hochladen');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await ticketApi.uploadAttachment(editingId, fd);
      setAttachments((prev) => [...prev, res.data]);
      toast.success(`${file.name} hochgeladen`);
      await load();
      // Direkt nach Upload: KI-Extraktion anbieten
      if (window.confirm(`Soll die KI ${file.name} automatisch auslesen und Felder vorausfüllen?`)) {
        await extractFromAttachment(res.data);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Anhang „${att.filename}" entfernen?`)) return;
    try {
      await ticketApi.deleteAttachment(editingId, att.file_id);
      setAttachments((prev) => prev.filter((a) => a.file_id !== att.file_id));
      toast.success('Anhang entfernt');
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const extractFromAttachment = async (att) => {
    setExtracting(att.file_id);
    try {
      toast.info('KI liest den Bescheid… das kann ~10 Sekunden dauern.');
      const res = await ticketApi.extractFromAttachment(editingId, att.file_id);
      const ex = res.data.extracted || {};
      // Merge only truthy/non-null fields, don't overwrite user input with null
      const merge = { ...form };
      if (ex.offense_datetime) merge.offense_date = ex.offense_datetime.substring(0, 16);
      if (ex.case_number) merge.case_number = ex.case_number;
      if (ex.authority) merge.authority = ex.authority;
      if (ex.offense_location) merge.offense_location = ex.offense_location;
      if (ex.offense_type && ['parken','geschwindigkeit','rotlicht','handynutzung','abstand','sonstige'].includes(ex.offense_type)) {
        merge.offense_type = ex.offense_type;
      }
      if (ex.offense_description) merge.offense_description = ex.offense_description;
      if (typeof ex.fine_amount === 'number') merge.fine_amount = ex.fine_amount;
      if (typeof ex.administrative_fee === 'number') merge.administrative_fee = ex.administrative_fee;
      setForm(merge);
      const filled = Object.entries(ex).filter(([, v]) => v != null && v !== '').length;
      toast.success(`KI hat ${filled} Felder erkannt und vorausgefüllt`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setExtracting(null); }
  };
  const save = async () => {
    try {
      const payload = {
        ...form,
        fine_amount: parseFloat(form.fine_amount) || 0,
        administrative_fee: parseFloat(form.administrative_fee) || 0,
        processing_fee: parseFloat(form.processing_fee) || 0,
        offense_date: new Date(form.offense_date).toISOString(),
        vehicle_id: form.vehicle_id || null,
        customer_id: form.customer_id || null,
        booking_id: form.booking_id || null,
      };
      if (editingId) {
        await ticketApi.update(editingId, payload);
        toast.success('Strafzettel aktualisiert');
      } else {
        await ticketApi.create(payload);
        toast.success('Strafzettel erfasst');
      }
      setShowForm(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Strafzettel ${t.ticket_number} löschen?`)) return;
    try { await ticketApi.remove(t.id); toast.success('Gelöscht'); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const notify = async (t) => {
    if (!window.confirm(`E-Mail an ${t.customer_name || 'Kunde'} senden?`)) return;
    try { const r = await ticketApi.notifyCustomer(t.id); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const charge = async (t) => {
    const total = (t.fine_amount || 0) + (t.processing_fee || 0) + (t.administrative_fee || 0);
    if (!window.confirm(`${eur(total)} als Kassenbuch-Einnahme verbuchen?`)) return;
    try { const r = await ticketApi.chargeCustomer(t.id); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Ticket weight="fill" size={26} className="text-[#E60000]" /> Strafzettel
          </h1>
          <p className="text-sm text-[#525866]">Erfassen · Halterauskunft · Kunden-Handling · Auswertung</p>
        </div>
        <Button className="btn-primary" onClick={openNew} data-testid="new-ticket-btn">
          <Plus size={14} className="mr-1" /> Neuer Strafzettel
        </Button>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-swiss p-3">
            <p className="overline text-[#8A94A6]">Gesamt</p>
            <p className="text-2xl font-bold text-[#111418]" data-testid="stat-total">{stats.total_tickets}</p>
          </div>
          <div className="card-swiss p-3">
            <p className="overline text-[#8A94A6]">Bußgelder</p>
            <p className="text-2xl font-bold text-[#E60000]">{eur(stats.total_fines)}</p>
          </div>
          <div className="card-swiss p-3">
            <p className="overline text-[#8A94A6]">Bearbeitungsgeb.</p>
            <p className="text-2xl font-bold text-[#009951]">{eur(stats.total_processing)}</p>
          </div>
          <div className="card-swiss p-3">
            <p className="overline text-[#8A94A6]">Offen / Überfällig</p>
            <p className="text-2xl font-bold text-[#FFB800]">
              {reminders?.open_count || 0}
              {reminders?.overdue?.length > 0 && (
                <span className="text-xs text-[#E60000] ml-1">({reminders.overdue.length} überfällig)</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <Label className="overline mr-2">Status-Filter:</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[220px]" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="card-swiss overflow-x-auto">
        <table className="w-full text-sm" data-testid="tickets-table">
          <thead className="bg-[#111418] text-white">
            <tr>
              <th className="text-left p-2 font-semibold">Nr.</th>
              <th className="text-left p-2 font-semibold">Datum</th>
              <th className="text-left p-2 font-semibold">Vergehen</th>
              <th className="text-left p-2 font-semibold">Fahrzeug / Kunde</th>
              <th className="text-right p-2 font-semibold">Bußgeld</th>
              <th className="text-right p-2 font-semibold">Bearb.</th>
              <th className="text-center p-2 font-semibold">Status</th>
              <th className="text-right p-2 font-semibold">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-[#8A94A6]">Keine Strafzettel erfasst.</td></tr>
            )}
            {tickets.map((t) => {
              const overdue = t.due_date && t.due_date < new Date().toISOString().substring(0, 10) && !['paid', 'closed'].includes(t.status);
              return (
                <tr key={t.id} className="border-t border-[#E2E4E9] even:bg-[#F7F8FA]" data-testid={`ticket-row-${t.id}`}>
                  <td className="p-2 font-mono font-semibold text-[#111418]">{t.ticket_number}</td>
                  <td className="p-2">
                    <div className="text-[#111418]">{(t.offense_date || '').substring(0, 10)}</div>
                    {overdue && <div className="text-[10px] text-[#E60000] font-bold flex items-center gap-1"><Warning size={10} weight="fill" /> Überfällig</div>}
                  </td>
                  <td className="p-2">
                    <div className="font-medium">{OFFENSE_TYPES.find((o) => o.value === t.offense_type)?.label || t.offense_type}</div>
                    {t.offense_location && <div className="text-xs text-[#8A94A6]">{t.offense_location}</div>}
                    {(t.attachments?.length || 0) > 0 && (
                      <div className="text-[10px] text-[#3B82F6] font-bold flex items-center gap-1 mt-0.5">
                        <Paperclip size={9} weight="bold" /> {t.attachments.length} Anhang{t.attachments.length !== 1 ? 'e' : ''}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="text-[#111418]">{t.vehicle_label || '—'}</div>
                    <div className="text-xs text-[#525866]">{t.customer_name || '— nicht zugeordnet —'}</div>
                  </td>
                  <td className="p-2 text-right tabular-nums text-[#E60000] font-semibold">{eur(t.fine_amount)}</td>
                  <td className="p-2 text-right tabular-nums text-[#009951]">{eur(t.processing_fee)}</td>
                  <td className="p-2 text-center">
                    <span className={`inline-block px-2 py-1 rounded-sm text-[10px] font-bold ${STATUS_STYLE[t.status] || ''}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex justify-end gap-1">
                      {t.customer_id && (
                        <a
                          href={ticketApi.halterauskunftUrl(t.id)}
                          target="_blank" rel="noreferrer"
                          className="p-1.5 hover:bg-[#F1F2F4] rounded-sm text-[#E60000]"
                          title="Halterauskunft-PDF"
                          data-testid={`ticket-pdf-${t.id}`}
                        >
                          <FilePdf size={14} weight="fill" />
                        </a>
                      )}
                      {t.customer_id && t.status !== 'paid' && t.status !== 'closed' && (
                        <button className="p-1.5 hover:bg-[#F1F2F4] rounded-sm text-[#3B82F6]"
                          onClick={() => notify(t)} title="Kunde per E-Mail informieren"
                          data-testid={`ticket-notify-${t.id}`}>
                          <EnvelopeSimple size={14} weight="bold" />
                        </button>
                      )}
                      {t.status !== 'paid' && t.status !== 'closed' && (
                        <button className="p-1.5 hover:bg-[#F1F2F4] rounded-sm text-[#009951]"
                          onClick={() => charge(t)} title="Als Kassenbuch-Einnahme verbuchen"
                          data-testid={`ticket-charge-${t.id}`}>
                          <Money size={14} weight="bold" />
                        </button>
                      )}
                      <button className="p-1.5 hover:bg-[#F1F2F4] rounded-sm text-[#525866]" onClick={() => openEdit(t)} title="Bearbeiten"
                        data-testid={`ticket-edit-${t.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button className="p-1.5 hover:bg-[#F1F2F4] rounded-sm text-[#E60000]" onClick={() => remove(t)} title="Löschen"
                        data-testid={`ticket-delete-${t.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Top-Kunden + Top-Fahrzeuge */}
      {stats && (stats.top_customers.length > 0 || stats.top_vehicles.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats.top_customers.length > 0 && (
            <div className="card-swiss p-4" data-testid="top-customers">
              <h3 className="font-bold text-[#111418] mb-2 flex items-center gap-2"><ChartLineUp size={16} className="text-[#E60000]" /> Top 10 Kunden</h3>
              <div className="space-y-1">
                {stats.top_customers.map((c, i) => (
                  <div key={c.customer_id} className="flex justify-between items-center text-sm border-b border-[#E2E4E9] py-1.5">
                    <span><b>{i + 1}.</b> {c.customer_name || '—'}</span>
                    <span className="text-[#E60000]"><b>{c.count}</b> Tickets · {eur(c.total_fines)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {stats.top_vehicles.length > 0 && (
            <div className="card-swiss p-4" data-testid="top-vehicles">
              <h3 className="font-bold text-[#111418] mb-2 flex items-center gap-2"><ChartLineUp size={16} className="text-[#FFB800]" /> Top 10 Fahrzeuge</h3>
              <div className="space-y-1">
                {stats.top_vehicles.map((v, i) => (
                  <div key={v.vehicle_id} className="flex justify-between items-center text-sm border-b border-[#E2E4E9] py-1.5">
                    <span><b>{i + 1}.</b> {v.vehicle_label || '—'}</span>
                    <span className="text-[#FFB800]"><b>{v.count}</b> Tickets · {eur(v.total_fines)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="ticket-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E2E4E9] shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Ticket size={18} className="text-[#E60000]" />
              {editingId ? 'Strafzettel bearbeiten' : 'Neuer Strafzettel'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="overline">Tatzeitpunkt *</Label><Input type="datetime-local" value={form.offense_date} onChange={(e) => setForm({ ...form, offense_date: e.target.value })} data-testid="offense-date" /></div>
              <div>
                <Label className="overline">Vergehen</Label>
                <Select value={form.offense_type} onValueChange={(v) => setForm({ ...form, offense_type: v })}>
                  <SelectTrigger data-testid="offense-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{OFFENSE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="overline">Ort</Label><Input value={form.offense_location} onChange={(e) => setForm({ ...form, offense_location: e.target.value })} placeholder="z.B. Marktplatz 1, Konstanz" /></div>
              <div className="col-span-2"><Label className="overline">Beschreibung</Label><Textarea rows={2} value={form.offense_description} onChange={(e) => setForm({ ...form, offense_description: e.target.value })} placeholder="Details zum Vergehen…" /></div>
              <div><Label className="overline">Behörde</Label><Input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} placeholder="z.B. Bußgeldstelle Konstanz" /></div>
              <div><Label className="overline">Aktenzeichen</Label><Input value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} /></div>
              <div><Label className="overline">Bußgeld (€)</Label><Input type="number" min={0} step={0.5} value={form.fine_amount} onChange={(e) => setForm({ ...form, fine_amount: e.target.value })} data-testid="fine-amount" /></div>
              <div><Label className="overline">Behördengebühr (€)</Label><Input type="number" min={0} step={0.5} value={form.administrative_fee} onChange={(e) => setForm({ ...form, administrative_fee: e.target.value })} /></div>
              <div><Label className="overline">Bearbeitungsgebühr (€)</Label><Input type="number" min={0} step={0.5} value={form.processing_fee} onChange={(e) => setForm({ ...form, processing_fee: e.target.value })} data-testid="processing-fee" /></div>
              <div className="col-span-2 pt-2 border-t border-[#E2E4E9]">
                <p className="text-xs text-[#8A94A6] mb-2">Fahrzeug & Kunde werden anhand des Tatzeitpunkts automatisch aus der aktiven Buchung ermittelt. Überschreiben bei Bedarf:</p>
              </div>
              <div>
                <Label className="overline">Fahrzeug (überschreibt Auto-Zuordnung)</Label>
                <Select value={form.vehicle_id || 'auto'} onValueChange={(v) => setForm({ ...form, vehicle_id: v === 'auto' ? '' : v })}>
                  <SelectTrigger data-testid="vehicle-select"><SelectValue placeholder="— Auto-Zuordnung —" /></SelectTrigger>
                  <SelectContent className="max-h-64"><SelectItem value="auto">— Auto-Zuordnung —</SelectItem>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Kunde (überschreibt Auto-Zuordnung)</Label>
                <Select value={form.customer_id || 'auto'} onValueChange={(v) => setForm({ ...form, customer_id: v === 'auto' ? '' : v })}>
                  <SelectTrigger data-testid="customer-select"><SelectValue placeholder="— Auto-Zuordnung —" /></SelectTrigger>
                  <SelectContent className="max-h-64"><SelectItem value="auto">— Auto-Zuordnung —</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || `${c.first_name || ''} ${c.last_name || ''}`.trim()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="overline">Notiz</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

              {/* Attachments */}
              <div className="col-span-2 pt-3 border-t border-[#E2E4E9]" data-testid="attachments-section">
                <div className="flex items-center justify-between mb-2">
                  <Label className="overline flex items-center gap-1"><Paperclip size={12} /> Anhänge (Bescheid, Fotos)</Label>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!editingId || uploading}
                    data-testid="upload-attachment-btn"
                  >
                    <Plus size={12} className="mr-1" /> {uploading ? 'Lädt hoch…' : 'Datei hinzufügen'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
                {!editingId && (
                  <p className="text-[11px] text-[#8A94A6]">Bitte den Strafzettel zuerst speichern, dann kannst du Anhänge hochladen.</p>
                )}
                {editingId && attachments.length === 0 && (
                  <p className="text-[11px] text-[#8A94A6]">Noch keine Anhänge. Erlaubt: PDF, JPG, PNG. Nach dem Upload kann die <strong>KI die Felder automatisch aus dem Bescheid auslesen</strong> (Aktenzeichen, Datum, Ort, Bußgeld etc.).</p>
                )}
                {editingId && attachments.length > 0 && (
                  <p className="text-[11px] text-[#7C3AED] mb-2 flex items-center gap-1"><MagicWand size={11} weight="bold" /> Auf das Zauber-Icon klicken → KI extrahiert Aktenzeichen, Datum, Ort, Bußgeld etc. aus dem Bescheid.</p>
                )}
                {attachments.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {attachments.map((att) => {
                      const isImage = (att.content_type || '').startsWith('image/');
                      const url = ticketApi.fileUrl(att.storage_path);
                      return (
                        <div key={att.file_id} className="border border-[#E2E4E9] rounded-sm bg-white overflow-hidden" data-testid={`attachment-${att.file_id}`}>
                          <a href={url} target="_blank" rel="noreferrer" className="block bg-[#F7F8FA] h-24 flex items-center justify-center">
                            {isImage ? (
                              <img src={url} alt={att.filename} className="max-h-24 object-contain" />
                            ) : (
                              <FilePdf size={40} className="text-[#E60000]" weight="fill" />
                            )}
                          </a>
                          <div className="px-2 py-1.5 flex justify-between items-center gap-1">
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] truncate text-[#111418]" title={att.filename}>{att.filename}</p>
                              <p className="text-[10px] text-[#8A94A6]">{Math.round((att.size || 0) / 1024)} KB</p>
                            </div>
                            <button
                              className="text-[#7C3AED] disabled:opacity-40"
                              onClick={() => extractFromAttachment(att)}
                              disabled={extracting === att.file_id}
                              title="Mit KI Felder aus dem Bescheid übernehmen"
                              data-testid={`extract-attachment-${att.file_id}`}
                            >
                              <MagicWand size={13} weight={extracting === att.file_id ? 'fill' : 'bold'} />
                            </button>
                            <a href={url} target="_blank" rel="noreferrer" className="text-[#525866] hover:text-[#111418]" title="Öffnen">
                              <DownloadSimple size={12} weight="bold" />
                            </a>
                            <button className="text-[#E60000]" onClick={() => removeAttachment(att)} title="Entfernen" data-testid={`remove-attachment-${att.file_id}`}>
                              <X size={12} weight="bold" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#E2E4E9] bg-white shrink-0">
            <Button variant="outline" onClick={() => setShowForm(false)}><X size={14} className="mr-1" /> Abbrechen</Button>
            <Button className="btn-primary" onClick={save} data-testid="save-ticket-btn"><CheckCircle size={14} className="mr-1" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrafficTickets;
